import { useEffect, useMemo, useState, useRef } from "react";
import {
  Box,
  Grid,
  TextField,
  Autocomplete,
  Typography,
  Pagination,
  CircularProgress,
  Button,
} from "@mui/material";

import LoadingScreen from "components/Loading";
import SnackbarWrapper from "components/Snackbar";

import {
  getCompetencies,
  getCompetencyLevels,
  getJobs,
  getDepartments,
  getJobGroups,
  getCompetenciesToJobsWithGroupName,
  addCompetencyToJob,
  deleteCompetencyToJob,
  exportCompetencyToJob,
  getAllJobCompetencyPoints,
  createJobCompetencyPoint,
  updateJobCompetencyPoint,
  deleteJobCompetencyPoint,
} from "services/api";

import MDButton from "components/MDButton";
import ImportButton from "components/ImportButton";
import { CloudDownload } from "@mui/icons-material";

import JobCard from "../components/jobCard";
import JobGroupCard from "../components/jobGroupCard";
import CompetencyCard from "../components/competencyCard";
import AddCompetencyDialog from "../components/addCompetencyDialog";
import CompetencyPointDialog from "../components/competencyPointDialog";

import PropTypes from "prop-types";

const CompetencyToJobPage = ({ canEdit }) => {
  const snackbarRef = useRef();

  const [loading, setLoading] = useState(true);

  const [jobs, setJobs] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [competencies, setCompetencies] = useState([]);
  const [levels, setLevels] = useState([]);
  const [groups, setGroups] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [points, setPoints] = useState([]);

  const [selectedJobId, setSelectedJobId] = useState(null);

  const [filteredDept, setFilteredDept] = useState(null);
  const [filteredJob, setFilteredJob] = useState(null);

  // dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState(null);

  const [selectedCompetency, setSelectedCompetency] = useState(null);
  const [selectedLevel, setSelectedLevel] = useState(null);

  const [pointDialogOpen, setPointDialogOpen] = useState(false);
  const [selectedPoint, setSelectedPoint] = useState(null);

  const [exportLoading, setExportLoading] = useState(false);
  const [expandedGroupId, setExpandedGroupId] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  const [selectedMapping, setSelectedMapping] = useState(null);
  const [selectedPoints, setSelectedPoints] = useState([]);

  const handleToggleGroup = (groupId) => {
    setExpandedGroupId((prev) => (prev === groupId ? null : groupId));
  };

  // ---------------- FETCH ----------------
  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);

        const [comp, lvl, j, d, g, m, p] = await Promise.all([
          getCompetencies(),
          getCompetencyLevels(),
          getJobs(),
          getDepartments(),
          getJobGroups(),
          getCompetenciesToJobsWithGroupName(),
          getAllJobCompetencyPoints(),
        ]);

        // Set all data first
        const validData = {
          competencies: comp !== "error" ? comp : [],
          levels: lvl !== "error" ? lvl : [],
          jobs: j !== "error" ? j : [],
          departments: d !== "error" ? d : [],
          groups: g !== "error" ? g : [],
          mappings: m !== "error" ? m : [],
          points: p !== "error" ? p : [],
        };

        setCompetencies(validData.competencies);
        setLevels(validData.levels);
        setJobs(validData.jobs);
        setDepartments(validData.departments);
        setGroups(validData.groups);
        setMappings(validData.mappings);
        setPoints(validData.points);

        // Set initial selected job only if jobs are available
        if (validData.jobs.length > 0 && !selectedJobId) {
          setSelectedJobId(validData.jobs[0].jobId);
        }
      } catch (error) {
        console.error("Failed to load data:", error);
        snackbarRef.current?.showSnackbar("Failed to load data", "error");
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []); // Removed selectedJobId dependency to prevent infinite loop

  // Reset selectedJobId when filters change
  useEffect(() => {
    if (filteredDept || filteredJob) {
      const filtered = jobs.filter((j) => {
        if (filteredDept && j.departmentId !== filteredDept) return false;
        if (filteredJob && j.jobId !== filteredJob) return false;
        return true;
      });

      if (filtered.length > 0) {
        // If current selection is not in filtered results, select the first one
        if (!filtered.find((j) => j.jobId === selectedJobId)) {
          setSelectedJobId(filtered[0].jobId);
        }
      } else {
        setSelectedJobId(null);
      }
    }
  }, [filteredDept, filteredJob, jobs]);

  const handleToggleBW = async (mapping, newValue) => {
    // Optimistic update
    setMappings((prev) =>
      prev.map((m) => (m.id === mapping.id ? { ...m, bwActive: newValue } : m))
    );

    try {
      const payload = [
        {
          id: mapping.id,
          jobId: mapping.jobId,
          jobGroupId: mapping.jobGroupId,
          compLevelId: mapping.compLevelId,
          bwActive: newValue,
        },
      ];

      const result = await addCompetencyToJob(payload);

      if (result === "error") {
        // Revert on failure
        setMappings((prev) =>
          prev.map((m) => (m.id === mapping.id ? { ...m, bwActive: !newValue } : m))
        );
        snackbarRef.current?.showSnackbar("Failed to update BW mode", "error");
      }
    } catch (e) {
      // Revert on error
      setMappings((prev) =>
        prev.map((m) => (m.id === mapping.id ? { ...m, bwActive: !newValue } : m))
      );
      snackbarRef.current?.showSnackbar("Failed to update BW mode", "error");
    }
  };

  // ---------------- DERIVED ----------------

  const filteredJobs = useMemo(() => {
    if (!filteredDept && !filteredJob) {
      return jobs; // Return all jobs when no filters are applied
    }

    return jobs.filter((j) => {
      if (filteredDept && j.departmentId !== filteredDept) return false;
      if (filteredJob && j.jobId !== filteredJob) return false;
      return true;
    });
  }, [jobs, filteredDept, filteredJob]);

  const currentSelectedJob = useMemo(() => {
    return jobs.find((j) => j.jobId === selectedJobId) || null;
  }, [jobs, selectedJobId]);

  const getJobGroupsMapped = (jobId) => {
    if (!jobId) return [];

    // Get all groups that belong to this job
    const jobGroupList = groups.filter((g) => g.jobId === jobId);

    const mappedGroups = jobGroupList.map((g) => {
      const groupMappings = mappings.filter(
        (m) => m.jobId === jobId && m.jobGroupId === g.jobGroupId
      );

      return {
        ...g,
        mappings: groupMappings,
      };
    });

    // Check for unassigned mappings
    const assignedMappingIds = mappedGroups.flatMap((g) => g.mappings.map((m) => m.id));

    const unassignedMappings = mappings.filter(
      (m) => m.jobId === jobId && !assignedMappingIds.includes(m.id)
    );

    // Always include unassigned group, even if empty
    // This allows users to add competencies to jobs without groups
    mappedGroups.push({
      jobGroupId: `ungrouped-${jobId}`,
      jobGroup: "Unassigned",
      mappings: unassignedMappings || [],
    });

    return mappedGroups;
  };

  const cleanJobGroupId = (value) => {
    if (!value) return null;
    // If it's a string and contains "ungrouped", return null
    if (typeof value === "string" && value.includes("ungrouped")) {
      return null;
    }
    // If it's already a number, return it
    if (typeof value === "number") {
      return value;
    }
    // Try to parse as number
    const num = Number(value);
    return isNaN(num) ? null : num;
  };

  // ---------------- ADD / UPDATE ----------------

  const handleAdd = async () => {
    if (!selectedJob || !selectedGroup || !selectedCompetency || !selectedLevel) {
      snackbarRef.current?.showSnackbar("Please fill all fields", "warning");
      return;
    }

    setActionLoading(true);

    try {
      const payload = [
        {
          jobId: selectedJob.jobId,
          jobGroupId: cleanJobGroupId(selectedGroup.jobGroupId),
          compLevelId: selectedLevel.competencyLevelId,
        },
      ];
      console.log(payload);
      const res = await addCompetencyToJob(payload);

      if (res !== "error") {
        setMappings((prev) => [...prev, ...res]);
        snackbarRef.current?.showSnackbar("Added successfully", "success");
        setDialogOpen(false);

        // Reset dialog state
        setSelectedCompetency(null);
        setSelectedLevel(null);
      } else {
        snackbarRef.current?.showSnackbar("Failed to add competency", "error");
      }
    } catch (error) {
      console.error("Failed to add competency:", error);
      snackbarRef.current?.showSnackbar("Failed to add competency", "error");
    } finally {
      setActionLoading(false);
    }
  };

  // ---------------- DELETE ----------------

  const handleDelete = async (mapping) => {
    try {
      const res = await deleteCompetencyToJob(mapping.id);

      if (res !== "error") {
        setMappings((prev) => prev.filter((m) => m.id !== mapping.id));
        snackbarRef.current?.showSnackbar("Deleted successfully", "success");
      } else {
        snackbarRef.current?.showSnackbar("Failed to delete", "error");
      }
    } catch (error) {
      console.error("Failed to delete:", error);
      snackbarRef.current?.showSnackbar("Failed to delete", "error");
    }
  };

  // ---------------- POINTS ----------------

  const handleSavePoint = async (data) => {
    try {
      if (data.id) {
        await updateJobCompetencyPoint(data.id, data);
      } else {
        await createJobCompetencyPoint(data);
      }

      const refreshed = await getAllJobCompetencyPoints();
      if (refreshed !== "error") {
        setPoints(refreshed);

        // Update selected points for the dialog
        if (selectedMapping) {
          const updated = refreshed.filter((p) => p.compToJobId === selectedMapping.id);
          setSelectedPoints(updated);
        }

        snackbarRef.current?.showSnackbar("Point saved successfully", "success");
      }
    } catch (error) {
      console.error("Failed to save point:", error);
      snackbarRef.current?.showSnackbar("Failed to save point", "error");
    }
  };

  const handleDeletePoint = async (id) => {
    try {
      await deleteJobCompetencyPoint(id);

      const refreshed = await getAllJobCompetencyPoints();
      if (refreshed !== "error") {
        setPoints(refreshed);
        if (selectedMapping) {
          const updated = refreshed.filter((p) => p.compToJobId === selectedMapping.id);
          setSelectedPoints(updated);
        }
        snackbarRef.current?.showSnackbar("Point deleted successfully", "success");
      }
    } catch (error) {
      console.error("Failed to delete point:", error);
      snackbarRef.current?.showSnackbar("Failed to delete point", "error");
    }
  };

  // ---------------- UI ----------------

  if (loading) return <LoadingScreen />;

  return (
    <Box p={2}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
        gap={2}
        flexWrap="wrap"
      >
        {/* LEFT SIDE - FILTERS */}
        <Box display="flex" gap={2} flexWrap="wrap">
          <Autocomplete
            sx={{ minWidth: 250 }}
            options={departments}
            getOptionLabel={(o) => o?.departmentName || ""}
            value={departments.find((d) => d.departmentId === filteredDept) || null}
            onChange={(e, v) => setFilteredDept(v?.departmentId || null)}
            renderInput={(p) => <TextField {...p} label="Department" />}
            isOptionEqualToValue={(option, value) => option.departmentId === value.departmentId}
          />

          <Autocomplete
            sx={{ minWidth: 250 }}
            options={jobs}
            getOptionLabel={(o) => o?.jobName || ""}
            value={jobs.find((j) => j.jobId === filteredJob) || null}
            onChange={(e, v) => setFilteredJob(v?.jobId || null)}
            renderInput={(p) => <TextField {...p} label="Job" />}
            isOptionEqualToValue={(option, value) => option.jobId === value.jobId}
          />
        </Box>
        <Box display="flex" gap={1}>
          <MDButton
            startIcon={<CloudDownload />}
            disabled={exportLoading}
            onClick={async () => {
              setExportLoading(true);
              await exportCompetencyToJob();
              setExportLoading(false);
            }}
          >
            Export
          </MDButton>

          <ImportButton allowedTypes={["competencyToJob"]} disabled={!canEdit} />
        </Box>
      </Box>

      {/* JOB LIST SIDEBAR */}
      <Box display="flex" gap={2} mb={3}>
        <Box sx={{ minWidth: 250, maxWidth: 300 }}>
          <Typography variant="h6" mb={1}>
            Jobs
          </Typography>
          <Box sx={{ maxHeight: 600, overflow: "auto" }}>
            {filteredJobs.map((job) => (
              <Box
                key={job.jobId}
                onClick={() => setSelectedJobId(job.jobId)}
                sx={{
                  p: 1.5,
                  mb: 1,
                  borderRadius: 1,
                  cursor: "pointer",
                  bgcolor: selectedJobId === job.jobId ? "primary.light" : "grey.100",
                  color: selectedJobId === job.jobId ? "white" : "text.primary",
                  "&:hover": {
                    bgcolor: selectedJobId === job.jobId ? "primary.main" : "grey.200",
                  },
                }}
              >
                <Typography variant="body1">{job.jobName}</Typography>
              </Box>
            ))}
            {filteredJobs.length === 0 && (
              <Typography color="text.secondary">No jobs found</Typography>
            )}
          </Box>
        </Box>

        {/* MAIN CONTENT */}
        <Box
          sx={{
            flex: 1,
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {currentSelectedJob ? (
            <>
              {/* HEADER */}
              <Box
                sx={{
                  p: 3,
                  borderBottom: "1px solid",
                  borderColor: "divider",
                  bgcolor: "background.paper",
                }}
              >
                <Typography variant="h4" fontWeight={600}>
                  {currentSelectedJob.jobName}
                </Typography>

                <Typography color="text.secondary">
                  {departments.find((d) => d.departmentId === currentSelectedJob.departmentId)
                    ?.departmentName || "Department not found"}
                </Typography>
              </Box>

              {/* CONTENT */}
              <Box
                sx={{
                  flex: 1,
                  overflowY: "auto",
                  p: 2,
                }}
              >
                {getJobGroupsMapped(currentSelectedJob.jobId).map((group) => (
                  <Box key={group.jobGroupId} mb={2}>
                    <JobGroupCard
                      groupId={group.jobGroupId}
                      groupName={group.jobGroup || "Unassigned"}
                      competencyCount={group.mappings.length}
                      canEdit={canEdit}
                      expanded={expandedGroupId === group.jobGroupId}
                      onToggle={() => handleToggleGroup(group.jobGroupId)}
                      onAddCompetency={() => {
                        setSelectedJob(currentSelectedJob);
                        setSelectedGroup(group);
                        setSelectedCompetency(null);
                        setSelectedLevel(null);
                        setDialogOpen(true);
                      }}
                    >
                      {group.mappings.length > 0 ? (
                        group.mappings.map((m) => {
                          const level = levels.find((l) => l.competencyLevelId === m.compLevelId);
                          const competency = level
                            ? competencies.find((c) => c.competencyId === level.competencyId)
                            : null;
                          const compPoints = points.filter((p) => p.compToJobId === m.id);

                          return (
                            <CompetencyCard
                              key={m.id}
                              competency={competency}
                              level={level}
                              mapping={m}
                              points={compPoints}
                              whiteCount={compPoints.filter((p) => p.type === "WHITE").length}
                              blackCount={compPoints.filter((p) => p.type === "BLACK").length}
                              canEdit={canEdit}
                              onToggleBW={handleToggleBW}
                              onDelete={() => handleDelete(m)}
                              onManagePoints={() => {
                                setSelectedMapping(m);
                                setSelectedPoints(compPoints);
                                setPointDialogOpen(true);
                              }}
                            />
                          );
                        })
                      ) : (
                        <Box sx={{ p: 2, textAlign: "center" }}>
                          <Typography color="text.secondary">
                            No competencies added yet. Click Add Competency to get started.
                          </Typography>
                        </Box>
                      )}
                    </JobGroupCard>
                  </Box>
                ))}
              </Box>
            </>
          ) : (
            <Box
              sx={{
                flex: 1,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Typography color="text.secondary">Select a job to view competencies</Typography>
            </Box>
          )}
        </Box>
      </Box>

      {/* Add Competency Dialog */}
      <AddCompetencyDialog
        open={dialogOpen}
        competencies={competencies}
        levels={levels.filter(
          (l) => selectedCompetency && l.competencyId === selectedCompetency.competencyId
        )}
        selectedCompetency={selectedCompetency}
        selectedLevel={selectedLevel}
        onCompetencyChange={setSelectedCompetency}
        onLevelChange={setSelectedLevel}
        onClose={() => {
          setDialogOpen(false);
          setSelectedCompetency(null);
          setSelectedLevel(null);
        }}
        onSave={handleAdd}
      />

      {/* Point Dialog */}
      <CompetencyPointDialog
        open={pointDialogOpen}
        points={selectedPoints}
        mapping={selectedMapping}
        onClose={() => {
          setPointDialogOpen(false);
          setSelectedMapping(null);
          setSelectedPoints([]);
        }}
        onSave={handleSavePoint}
        onDelete={handleDeletePoint}
      />

      <SnackbarWrapper ref={snackbarRef} />
    </Box>
  );
};

CompetencyToJobPage.propTypes = {
  canEdit: PropTypes.bool.isRequired,
};

export default CompetencyToJobPage;
